/**
 * Deployment Logs Table - Audit trail of all deployments
 */

import { ReactTable } from "@aix/ui"

interface DeploymentLogsTableProps {
  logs: any[]
  isLoading: boolean
}

export function DeploymentLogsTable({ logs, isLoading }: DeploymentLogsTableProps) {
  if (isLoading) {
    return <div className="text-muted-foreground">Loading deployment logs...</div>
  }

  if (logs.length === 0) {
    return <div className="text-muted-foreground">No deployment logs found</div>
  }

  const getActionIcon = (action: string) => {
    switch (action) {
      case "deploy":
        return "🚀"
      case "rollback":
        return "↩️"
      case "delete":
        return "🗑️"
      case "view":
        return "👁️"
      default:
        return "📝"
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case "deploy":
        return "bg-green-100 text-green-800"
      case "rollback":
        return "bg-yellow-100 text-yellow-800"
      case "delete":
        return "bg-red-100 text-red-800"
      case "view":
        return "bg-blue-100 text-blue-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const columns = [
    {
      Header: "Action",
      accessor: "log.action",
      Cell: ({ row }: any) => (
        <span className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${getActionColor(row.log.action)}`}>
          <span>{getActionIcon(row.log.action)}</span>
          <span className="capitalize">{row.log.action}</span>
        </span>
      ),
    },
    {
      Header: "Message",
      accessor: "log.message",
      Cell: ({ row }: any) => (
        <div className="max-w-md">
          <p className="font-medium text-foreground truncate">{row.log.message}</p>
          <p className="text-xs text-muted-foreground mt-1">Update ID: {row.log.updateId}</p>
        </div>
      ),
    },
    {
      Header: "Channel",
      accessor: "log.channel",
      Cell: ({ row }: any) => (
        <span className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">{row.log.channel}</span>
      ),
    },
    {
      Header: "Project",
      accessor: "project.name",
      Cell: ({ row }: any) => (
        <div className="text-sm">
          <p className="text-foreground">{row.project?.name}</p>
          <p className="text-xs text-muted-foreground">@{row.project?.slug}</p>
        </div>
      ),
    },
    {
      Header: "Timestamp",
      accessor: "log.createdAt",
      Cell: ({ row }: any) => {
        const date = new Date(row.log.createdAt)
        return (
          <div className="text-sm">
            <p className="text-foreground">{date.toLocaleDateString()}</p>
            <p className="text-xs text-muted-foreground">{date.toLocaleTimeString()}</p>
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <ReactTable data={logs} columns={columns} />
    </div>
  )
}
